var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, Children, isValidElement, useEffect, memo, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
const TabPanel = styled(Box) `
    padding-top: 24px;
`;
export const SimpleTab = ({ children }) => {
    return children;
};
export const SimpleTabs = memo((props) => {
    const { children, defaultTabId, tabManager, panelProps } = props, simpleTabsProps = __rest(props, ["children", "defaultTabId", "tabManager", "panelProps"]);
    const [localTab, setLocalTab] = useState(defaultTabId);
    const initialTabSet = useRef(false); // Just used to track if the initial tab has been set.
    const selectedTab = useMemo(() => {
        if (tabManager !== undefined) {
            return tabManager.tabId;
        }
        return localTab;
    }, [tabManager, localTab]);
    const setSelectedTab = useMemo(() => (tabId) => {
        initialTabSet.current = true;
        if (tabManager !== undefined) {
            tabManager.setTabId(tabId);
            return;
        }
        setLocalTab(tabId);
    }, [tabManager]);
    const handleChange = (event, newTabId) => {
        setSelectedTab(newTabId);
    };
    // Filter out non-valid React elements before mapping
    const validChildren = Children.toArray(children).filter((child) => isValidElement(child) && child.props.id);
    const tabsProps = Object.assign(Object.assign({}, simpleTabsProps), { value: selectedTab, onChange: (event, newValue) => handleChange(event, newValue) });
    // Update the state if the children change
    useEffect(() => {
        if (defaultTabId || initialTabSet.current) {
            // Default should already be set.
            return;
        }
        const firstChild = Children.toArray(children)[0];
        if (firstChild && firstChild.props.id) {
            // Set initial state to the id of the first tab
            setSelectedTab(firstChild.props.id);
        }
    }, [defaultTabId, children, setSelectedTab]);
    if (!selectedTab || !validChildren.length) {
        return null;
    }
    return (_jsxs(Box, { children: [_jsx(Tabs, Object.assign({}, tabsProps, { children: validChildren.map((child) => {
                    // For each child, remove its children from props and use the rest of the props for the tab.
                    const _a = child.props, { children: unwanted } = _a, simpleTabProps = __rest(_a, ["children"]);
                    const tabProps = Object.assign(Object.assign({}, simpleTabProps), { label: child.props.label, 'data-testid': `tab-${child.props.id}`, id: `tab-${child.props.id}`, 'aria-controls': `tabpanel-${child.props.id}`, value: child.props.id, key: child.props.id });
                    return _jsx(Tab, Object.assign({}, tabProps));
                }) })), _jsx(Box, { children: validChildren.map((child) => {
                    const tabPanelProps = Object.assign(Object.assign({}, (panelProps !== null && panelProps !== void 0 ? panelProps : {})), { role: 'tabpanel', hidden: selectedTab !== child.props.id, 'data-testid': `tabpanel-${child.props.id}`, id: `tabpanel-${child.props.id}`, 'aria-labelledby': `tab-${child.props.id}`, key: `tabpanel-${child.props.id}` });
                    return _jsx(TabPanel, Object.assign({}, tabPanelProps, { children: selectedTab === child.props.id ? child : null }));
                }) })] }));
});
